// Pure date/string helpers shared by PickerField.tsx (native) and
// PickerField.web.tsx — kept in one place so the `YYYY-MM-DD`/`HH:mm`
// string contract and the future-date/min-max validation rules can never
// drift between platforms.

export function parseToDate(value: string, mode: 'date' | 'time'): Date {
  const now = new Date();
  if (!value) return now;
  if (mode === 'date') {
    const parts = value.split('-').map(Number);
    if (parts.length === 3 && !parts.some(isNaN)) {
      return new Date(parts[0], parts[1] - 1, parts[2]);
    }
    return now;
  }
  const parts = value.split(':').map(Number);
  if (parts.length >= 2 && !parts.some(isNaN)) {
    const d = new Date();
    d.setHours(parts[0], parts[1], 0, 0);
    return d;
  }
  return now;
}

export function dateToString(date: Date, mode: 'date' | 'time'): string {
  if (mode === 'date') {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }
  const h = String(date.getHours()).padStart(2, '0');
  const min = String(date.getMinutes()).padStart(2, '0');
  return `${h}:${min}`;
}

export function timeMinutesBefore(candidate: Date, minimum: Date): boolean {
  return (
    candidate.getHours() * 60 + candidate.getMinutes() <
    minimum.getHours() * 60 + minimum.getMinutes()
  );
}

export function dayOnly(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

export function isDateBefore(candidate: Date, minimum: Date): boolean {
  return dayOnly(candidate).getTime() < dayOnly(minimum).getTime();
}

export function isDateAfter(candidate: Date, maximum: Date): boolean {
  return dayOnly(candidate).getTime() > dayOnly(maximum).getTime();
}

export function clampToMinimum(date: Date, mode: 'date' | 'time', minimumDate?: Date, maximumDate?: Date): Date {
  if (mode !== 'date') return date;
  if (minimumDate && isDateBefore(date, minimumDate)) return minimumDate;
  if (maximumDate && isDateAfter(date, maximumDate)) return maximumDate;
  return date;
}

export function formatDisplay(value: string, mode: 'date' | 'time', locale: string): string {
  if (!value) return '';
  if (mode === 'date') {
    const date = parseToDate(value, 'date');
    return date.toLocaleDateString(locale, { day: 'numeric', month: 'short', year: 'numeric' });
  }
  return value;
}

export interface PickerFieldProps {
  value: string;
  onChange: (val: string) => void;
  onReject?: () => void;
  placeholder: string;
  mode: 'date' | 'time';
  minimumDate?: Date;
  maximumDate?: Date;
  noMargin?: boolean;
}

export const PICKER_LOCALE_BY_LANGUAGE: Record<string, string> = {
  de: 'de-DE',
  es: 'es-ES',
  fr: 'fr-FR',
  ru: 'ru-RU',
};
